import type { WorkflowDefinition } from "../types";

/**
 * W5 — Monthly client reporting.
 *
 * `perTarget` on a period: a report is about one month, a new run opens each
 * month, and last month's run stays readable while this month's is in flight.
 * The month is keyed `YYYY-MM` by the start dialog, so two people preparing the
 * same month's report continue the same run.
 *
 * The `since` predicates stay property-wide rather than target-scoped. An
 * annotation and an export are property-timeline acts with no per-month slice
 * behind them, and anchoring them to `run.startedAt` already gives the step
 * what it needs: work logged *since this month's run opened*.
 */
export const clientReportingWorkflow: WorkflowDefinition = {
	id: "client-reporting",
	wNumber: 5,
	title: "Build a monthly AI visibility report",
	outcome:
		"Build one branded report a month where every change on the chart has a reason.",
	audiences: ["agencies", "inhouse"],
	uses: ["Annotations", "Settings", "Visibility", "Projects", "AI Reports"],
	scope: "property",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "period", label: "Reporting month" },
	isApplicable: (mode) => mode.showProjects,
	articleSlug: "how-to-build-monthly-ai-visibility-client-reports",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "log-the-work",
			title: "Note the month's work as it happens",
			page: "Annotations",
			route: "/annotations",
			criterion: "You've added at least one note since you started.",
			why: "AI visibility lags the work that caused it by one to three weeks. By month end nobody remembers what happened when.",
			docsHref: "/docs/dashboards/annotations",
			tasks: [
				"Note each publish, fix and landed pitch the day it happens",
				"Note what you did not do too, like a rival launch",
				"Keep it current so month end is just reading",
			],
			complete: { kind: "since", event: "annotationCreated" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Events on the timeline",
							value: state.property.manualAnnotationCount,
							target: 1,
						}
					: null,
		},
		{
			id: "schedule-report",
			title: "Set the monthly report to send itself",
			page: "Settings",
			route: "/settings?tab=reports",
			criterion: "You've set reports to send automatically.",
			why: "Set reports to send automatically on the same day each month. A 29-day window against a 39-day one is not a trend.",
			docsHref: "/docs/dashboards/settings",
			tasks: [
				"Set a monthly schedule a few days before the review call",
				"Check who gets it on the Notifications tab. Everyone on the team is the default.",
			],
			complete: {
				kind: "all",
				of: [
					{ kind: "flag", key: "hasReportSchedule" },
					{ kind: "count", key: "reportRecipientCount", gte: 1 },
				],
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Schedule set",
									done: state.property.hasReportSchedule,
								},
								{
									label: `Sent to ${state.property.reportRecipientCount} ${state.property.reportRecipientCount === 1 ? "person" : "people"}`,
									done:
										state.property.reportRecipientCount > 0,
								},
							],
						}
					: null,
		},
		{
			id: "connect-the-numbers",
			title: "Explain what moved and why",
			page: "Visibility",
			route: "/dashboard/visibility",
			criterion: "You've got daily data for the month.",
			why: "Read the trend lines next to your notes. Some moves are yours, some are not, and some have no explanation yet.",
			docsHref: "/docs/dashboards/brand-dashboard",
			tasks: [
				"Open the trend lines with your notes on the same chart",
				"Write one sentence for each move worth reporting",
				"Check the confidence band before claiming a win",
			],
			complete: { kind: "flag", key: "hasNightlyMetrics" },
			enrichment: "sovTrend",
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Daily data flowing",
									done: state.property.hasNightlyMetrics,
								},
								{
									label: `${state.property.manualAnnotationCount} events to explain it with`,
									done:
										state.property.manualAnnotationCount >
										0,
								},
							],
						}
					: null,
		},
		{
			id: "export-deliverable",
			title: "Build one report, not five screenshots",
			page: "Projects",
			route: "/projects",
			criterion:
				"You've exported a report or a project since you started.",
			why: "One document in the same shape every month, with your branding on it. That is your analysis, not a tool's output.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Export the report or the project detail as PDF or slides",
				"Put a short written story in front of the charts",
				"Send it the same way every month",
			],
			complete: {
				kind: "any",
				of: [
					{ kind: "since", event: "reportExported" },
					{ kind: "since", event: "projectExported" },
				],
			},
			signal: ({ state }) => ({
				kind: "checklist",
				items: [
					{
						label: "Your branding on the export",
						done: state.organization.hasWhiteLabelBranding,
					},
					{
						label: `${state.property?.completedProjectCount ?? 0} projects completed to date`,
						done: (state.property?.completedProjectCount ?? 0) > 0,
					},
				],
			}),
		},
	],
};
